import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ForceGraph2D, { ForceGraphMethods, NodeObject, LinkObject } from 'react-force-graph-2d';
import {
  performanceMetricsService,
  GraphLinksResponse,
} from '../../../../services/performanceMetricsService';
import { ColorBy, SizeBy, FGNode, FGLink, getCategoryColor, getCommunityColor, getNodeRadius, decodeHtml } from './linkGraphUtils';
import { LinkGraphLegend } from './LinkGraphLegend';
import { buildNodeTooltip, buildLinkTooltip } from './LinkGraphTooltips';

/** Graph 1: Internal links between articles. */
export const LinkGraph: React.FC = () => (
  <ForceGraphView
    title="Internal Link Network"
    fetchFn={() => performanceMetricsService.getGraphLinks()}
    edgeLabelKey="anchors"
    edgeLabelName="Anchor text"
  />
);

/** Graph 2: GSC keyword overlap (exact match, impressions-based). */
export const QueryGraph: React.FC = () => (
  <ForceGraphView
    title="Keyword Overlap Network"
    fetchFn={() => performanceMetricsService.getGraphQueries()}
    edgeLabelKey="shared_queries"
    edgeLabelName="Shared queries"
  />
);

const HEIGHT = 700;

type ForceGraphViewProps = {
  title: string;
  fetchFn: () => Promise<GraphLinksResponse>;
  edgeLabelKey: string;
  edgeLabelName: string;
};

const ForceGraphView: React.FC<ForceGraphViewProps> = ({ title, fetchFn, edgeLabelKey, edgeLabelName }) => {
  const graphRef = useRef<ForceGraphMethods | undefined>();
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(1100);

  const [data, setData] = useState<GraphLinksResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [colorBy, setColorBy] = useState<ColorBy>('community');
  const [sizeBy, setSizeBy] = useState<SizeBy>('clicks');
  const [showOrphans, setShowOrphans] = useState(false);
  const [hoveredKey, setHoveredKey] = useState<string | number | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | number | null>(null);
  const activeKey = selectedKey ?? hoveredKey;
  const [tooltip, setTooltip] = useState<{ x: number; y: number; html: React.ReactNode } | null>(null);

  const nodeMatchesHighlight = useCallback((n: FGNode): boolean => {
    if (activeKey == null) return true;
    if (colorBy === 'community' || colorBy === 'sub_community') return n.community === activeKey;
    if (colorBy === 'category') return n.category === activeKey;
    return true;
  }, [activeKey, colorBy]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetchFn();
        setData(resp);
      } catch (e: any) {
        setError(e?.message || 'Failed to load link graph');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const graphData = useMemo(() => {
    if (!data) return { nodes: [] as FGNode[], links: [] as FGLink[] };
    const allNodes: FGNode[] = [
      ...data.nodes.map((n) => ({ ...n, isOrphan: false })),
      ...(showOrphans ? data.orphans.map((n) => ({ ...n, isOrphan: true })) : []),
    ];
    const nodeIds = new Set(allNodes.map((n) => n.id));
    const links: FGLink[] = data.edges
      .filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target))
      .map((e) => ({
        source: e.source,
        target: e.target,
        labels: (e as any)[edgeLabelKey] || e.anchors || [],
      }));
    return { nodes: allNodes, links };
  }, [data, showOrphans]);

  const nodeColor = useCallback((node: NodeObject) => {
    const n = node as FGNode;
    if (n.isOrphan) return '#e2e8f0';
    if (colorBy === 'category') return getCategoryColor(n.category);
    if (colorBy === 'community') return getCommunityColor(n.community);
    if (colorBy === 'sub_community') return getCommunityColor(n.sub_community);
    return '#64748b';
  }, [colorBy]);

  const paintNode = useCallback((node: NodeObject, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const n = node as FGNode;
    const r = getNodeRadius(n, sizeBy);
    const x = n.x ?? 0;
    const y = n.y ?? 0;
    const dimmed = activeKey != null && !nodeMatchesHighlight(n);

    ctx.beginPath();
    ctx.arc(x, y, r, 0, 2 * Math.PI);
    ctx.fillStyle = nodeColor(n);
    ctx.globalAlpha = dimmed ? 0.08 : n.isOrphan ? 0.4 : 0.85;
    ctx.fill();
    ctx.globalAlpha = 1;

    if (n.isOrphan && !dimmed) {
      ctx.strokeStyle = '#cbd5e1';
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }

    if (globalScale > 2.5 && !dimmed) {
      const label = decodeHtml(n.title).slice(0, 30);
      ctx.font = `${Math.max(3, 10 / globalScale)}px sans-serif`;
      ctx.fillStyle = '#374151';
      ctx.textAlign = 'center';
      ctx.fillText(label, x, y + r + 8 / globalScale);
    }
  }, [sizeBy, nodeColor, activeKey, nodeMatchesHighlight]);

  const linkColor = useCallback((link: LinkObject) => {
    if (activeKey == null) return 'rgba(148, 163, 184, 0.3)';
    const src = link.source as FGNode;
    const tgt = link.target as FGNode;
    if (nodeMatchesHighlight(src) || nodeMatchesHighlight(tgt)) return 'rgba(148, 163, 184, 0.5)';
    return 'rgba(148, 163, 184, 0.04)';
  }, [activeKey, nodeMatchesHighlight]);
  const linkWidth = useCallback(() => 0.5, []);

  const handleNodeHover = useCallback((node: NodeObject | null, prevNode: NodeObject | null) => {
    if (!node) { setTooltip(null); setHoveredKey(null); return; }
    const n = node as FGNode;
    if (selectedKey == null) {
      if (colorBy === 'category') setHoveredKey(n.category);
      else setHoveredKey(n.community);
    }
    const screen = graphRef.current?.graph2ScreenCoords(n.x ?? 0, n.y ?? 0);
    if (!screen) return;
    const rect = containerRef.current?.getBoundingClientRect();
    setTooltip({
      x: (rect?.left ?? 0) + screen.x + 15,
      y: (rect?.top ?? 0) + screen.y - 10,
      html: buildNodeTooltip(n),
    });
  }, []);

  const handleLinkHover = useCallback((link: LinkObject | null) => {
    if (!link) { setTooltip(null); return; }
    const l = link as unknown as FGLink;
    const src = l.source as FGNode;
    const tgt = l.target as FGNode;
    if (!src?.x || !tgt?.x) return;
    const midX = ((src.x ?? 0) + (tgt.x ?? 0)) / 2;
    const midY = ((src.y ?? 0) + (tgt.y ?? 0)) / 2;
    const screen = graphRef.current?.graph2ScreenCoords(midX, midY);
    if (!screen) return;
    const rect = containerRef.current?.getBoundingClientRect();
    setTooltip({
      x: (rect?.left ?? 0) + screen.x + 15,
      y: (rect?.top ?? 0) + screen.y - 10,
      html: buildLinkTooltip(src, tgt, l.labels, edgeLabelName),
    });
  }, []);

  const handleNodeClick = useCallback((node: NodeObject) => {
    const n = node as FGNode;
    const key = colorBy === 'category' ? n.category : n.community;
    if (key == null) return;
    setSelectedKey((prev) => (prev === key ? null : key));
  }, [colorBy]);

  const handleBackgroundClick = useCallback(() => { setSelectedKey(null); }, []);

  return (
    <div ref={containerRef}>
      {/* Controls */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-4">
          <h2 className="text-sm font-medium text-gray-700">{title}</h2>
          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
            <input type="checkbox" checked={showOrphans} onChange={(e) => setShowOrphans(e.target.checked)} className="rounded border-gray-300" />
            Show orphans ({data?.orphan_count ?? 0})
          </label>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <label className="flex items-center gap-1.5 text-gray-500">
            Color:
            <select value={colorBy} onChange={(e) => { setColorBy(e.target.value as ColorBy); setSelectedKey(null); setHoveredKey(null); }}
              className="border border-gray-300 rounded px-1.5 py-0.5 text-gray-700">
              <option value="none">None</option>
              <option value="category">WP Category</option>
              <option value="community">Community (auto)</option>
              <option value="sub_community">Sub-community</option>
            </select>
          </label>
          <label className="flex items-center gap-1.5 text-gray-500">
            Size:
            <select value={sizeBy} onChange={(e) => setSizeBy(e.target.value as SizeBy)}
              className="border border-gray-300 rounded px-1.5 py-0.5 text-gray-700">
              <option value="uniform">Uniform</option>
              <option value="clicks">Clicks</option>
              <option value="impressions">Impressions</option>
            </select>
          </label>
          <span className="text-gray-400">
            {data ? `${data.node_count} nodes · ${data.edge_count} links · ${data.community_count} communities` : ''}
            {selectedKey != null && (
              <span className="ml-2 text-cyan-600">
                · {typeof selectedKey === 'number' ? `Community ${selectedKey}` : selectedKey} selected
                <button onClick={() => setSelectedKey(null)} className="ml-1 text-gray-400 hover:text-gray-600">&times;</button>
              </span>
            )}
          </span>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-500" />
        </div>
      ) : (
        <div className="relative border border-gray-200 rounded bg-gray-50 overflow-hidden">
          <ForceGraph2D
            ref={graphRef}
            width={width}
            height={HEIGHT}
            graphData={graphData}
            nodeCanvasObject={paintNode}
            nodePointerAreaPaint={(node, color, ctx) => {
              const n = node as FGNode;
              const r = getNodeRadius(n, sizeBy) + 2;
              ctx.beginPath();
              ctx.arc(n.x ?? 0, n.y ?? 0, r, 0, 2 * Math.PI);
              ctx.fillStyle = color;
              ctx.fill();
            }}
            linkColor={linkColor}
            linkWidth={linkWidth}
            onNodeHover={handleNodeHover}
            onNodeClick={handleNodeClick}
            onBackgroundClick={handleBackgroundClick}
            onLinkHover={handleLinkHover}
            onNodeDragEnd={(node) => { node.fx = undefined; node.fy = undefined; }}
            d3AlphaDecay={0.01}
            d3VelocityDecay={0.3}
            warmupTicks={100}
            cooldownTicks={200}
          />

          {data && colorBy !== 'none' && (
            <LinkGraphLegend
              data={data}
              colorBy={colorBy}
              showOrphans={showOrphans}
              selectedKey={selectedKey}
              setSelectedKey={setSelectedKey}
            />
          )}

          <div className="absolute top-3 right-3 text-[10px] text-gray-400">
            Scroll to zoom · Drag to pan · Drag nodes to reposition
          </div>
        </div>
      )}

      {tooltip && (
        <div
          className="fixed z-50 bg-white shadow-lg border border-gray-200 rounded-lg px-3 py-2 pointer-events-none max-w-xs"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          {tooltip.html}
        </div>
      )}
    </div>
  );
};
